/**
 * Storm Response marketing page. The call to action is the 7-day free trial.
 *
 * Six sections, each a dedicated box (copy + illustrative mock-up):
 *   1 The outcome  2 The tap and call  3 The search intent
 *   4 The safety net  5 Zero manual work  6 Reverse onboarding  (+ FAQ)
 *
 * Every claim describes behaviour that exists in the product. The mock-ups are
 * labelled as illustrations. Do not add statistics, speed promises or revenue
 * numbers unless they come from real pilot data.
 */
import type { ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  CloudLightning,
  Globe,
  Layers,
  MapPin,
  Phone,
  PhoneCall,
  Search,
  ShieldCheck,
  Sparkles,
  Wand2,
} from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getAuthUrl } from "@/utils/redirectUtils";

// Signup/login lives on the app domain. (Do not link to app.upfixe.com/storm-response:
// logged-out visitors there are sent back to this page.)
const TRIAL_URL = getAuthUrl();

/* ── layout helpers ─────────────────────────────────────────────────────── */

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 },
};

function Section({
  index,
  label,
  title,
  children,
  mock,
  flip = false,
  tinted = false,
}: {
  index: number;
  label: string;
  title: ReactNode;
  children: ReactNode;
  mock: ReactNode;
  flip?: boolean;
  tinted?: boolean;
}) {
  return (
    <section className={`py-16 md:py-24 px-4 ${tinted ? "bg-muted/40 border-y" : ""}`}>
      <div className="container mx-auto max-w-6xl">
        <motion.div
          {...fade}
          className="grid md:grid-cols-2 gap-10 md:gap-16 items-center"
        >
          <div className={flip ? "md:order-2" : ""}>
            <Badge variant="secondary" className="mb-4 gap-1.5">
              <span className="font-semibold">{index}</span> · {label}
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4 leading-tight">
              {title}
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">{children}</div>
          </div>
          <div className={flip ? "md:order-1" : ""}>{mock}</div>
        </motion.div>
      </div>
    </section>
  );
}

function Ticks({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 pt-1">
      {items.map((t) => (
        <li key={t} className="flex gap-2 text-sm text-foreground">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function MockCard({ caption, children }: { caption: string; children: ReactNode }) {
  return (
    <div>
      <div className="rounded-2xl border border-border bg-card shadow-lg p-5 md:p-6">{children}</div>
      <p className="mt-2 text-center text-xs text-muted-foreground">{caption}</p>
    </div>
  );
}

/* ── mock-ups (illustrations only) ──────────────────────────────────────── */

function PhoneMock() {
  return (
    <MockCard caption="Illustration. Your phone, after a storm.">
      <div className="mx-auto max-w-xs rounded-3xl border-4 border-foreground/80 bg-background p-4 space-y-3">
        <div className="text-center text-xs text-muted-foreground">Incoming call</div>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <PhoneCall className="h-6 w-6" />
          </div>
          <div>
            <div className="font-semibold text-foreground">Ad call: Hail damage</div>
            <div className="text-xs text-muted-foreground">Homeowner · Plano, TX</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <div className="rounded-xl bg-red-100 text-red-700 text-center py-2 text-sm font-medium">Decline</div>
          <div className="rounded-xl bg-green-100 text-green-700 text-center py-2 text-sm font-medium">Answer</div>
        </div>
      </div>
    </MockCard>
  );
}

function AdMock() {
  return (
    <MockCard caption="Illustration of a Google search ad. Your wording and pages will differ.">
      <div className="space-y-3 text-sm">
        <div className="flex items-center gap-2 rounded-full border px-3 py-2 text-muted-foreground">
          <Search className="h-4 w-4" /> emergency roof repair near me
        </div>
        <div className="space-y-1">
          <div className="text-xs text-foreground">
            <span className="font-semibold">Ad</span> · yourroofer.com/hail-damage/plano
          </div>
          <div className="text-lg text-blue-700 font-medium leading-snug">
            Hail Damage? Free Roof Inspection | Your Roofer
          </div>
          <div className="text-muted-foreground">
            Licensed and insured. Storm damage repair for Plano homeowners. Call now.
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-blue-700">
            <span>Free Inspection</span>
            <span>Storm Damage Repair</span>
            <span>Plano Roofing</span>
            <span>Reviews</span>
          </div>
          <div className="pt-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-foreground">
              <Phone className="h-3.5 w-3.5" /> Call
            </span>
          </div>
        </div>
      </div>
    </MockCard>
  );
}

function IntentMock() {
  const groups = [
    { name: "Hail Damage", words: ["hail damage roof repair", "emergency roof repair", "storm damage roofing"] },
    { name: "Plano & Near Me", words: ["roofer near me", "roof repair plano tx", "local roofing company"] },
    { name: "Roofing Services", words: ["roof replacement", "roof leak repair", "your company name"] },
  ];
  return (
    <MockCard caption="Illustration. Three ad groups: weather, location, services.">
      <div className="space-y-4">
        {groups.map((g) => (
          <div key={g.name}>
            <div className="text-sm font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5 text-primary" /> {g.name}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {g.words.map((w) => (
                <span key={w} className="rounded-full bg-muted px-2.5 py-1 text-xs text-foreground">
                  {w}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </MockCard>
  );
}

function GuardrailMock() {
  const rows: [string, string][] = [
    ["Daily budget", "$ you choose"],
    ["Cost per lead", "$ you choose"],
    ["Monthly limit (Google + Bing)", "$ you choose"],
    ["Campaign length", "Ends by itself"],
  ];
  return (
    <MockCard caption="Illustration of your settings.">
      <div className="space-y-3 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between border-b pb-2 last:border-0">
            <span className="text-foreground">{k}</span>
            <span className="text-muted-foreground">{v}</span>
          </div>
        ))}
        <div className="flex items-center justify-between rounded-xl bg-primary/10 px-3 py-2.5">
          <span className="flex items-center gap-2 text-foreground font-medium">
            <ShieldCheck className="h-4 w-4 text-primary" /> Approve each campaign
          </span>
          <span className="rounded-full bg-primary text-primary-foreground text-xs px-2.5 py-0.5">On</span>
        </div>
      </div>
    </MockCard>
  );
}

function ScanMock() {
  return (
    <MockCard caption="Illustration. We suggest, you review and save.">
      <div className="space-y-3 text-sm">
        <div className="flex items-center gap-2 rounded-xl border px-3 py-2 text-muted-foreground">
          <Globe className="h-4 w-4 text-primary" /> yourroofer.com
          <span className="ml-auto inline-flex items-center gap-1 rounded-lg bg-primary text-primary-foreground px-2.5 py-1 text-xs">
            <Wand2 className="h-3 w-3" /> Scan
          </span>
        </div>
        {[
          ["Phone number", "Found on your site"],
          ["What makes you the best", "Drafted from your site's own claims"],
          ["Landing pages", "City and service pages we found"],
        ].map(([k, v]) => (
          <div key={k} className="rounded-xl bg-muted/60 px-3 py-2">
            <div className="text-xs text-muted-foreground">{k}</div>
            <div className="text-foreground">{v}</div>
          </div>
        ))}
      </div>
    </MockCard>
  );
}

function StepsMock() {
  const steps = [
    { icon: Globe, t: "Connect Google and Microsoft Ads", d: "Ads run in your own accounts." },
    { icon: MapPin, t: "Set your service area", d: "Cities or ZIP codes you cover." },
    { icon: Phone, t: "Choose calls or clicks", d: "Then set your budget and limits." },
    { icon: CloudLightning, t: "Armed and ready", d: "Next storm in your area, we prepare the ads." },
  ];
  return (
    <MockCard caption="What setup looks like.">
      <ol className="space-y-4">
        {steps.map(({ icon: Icon, t, d }, i) => (
          <li key={t} className="flex gap-3">
            <div className="h-9 w-9 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">
                {i + 1}. {t}
              </div>
              <div className="text-xs text-muted-foreground">{d}</div>
            </div>
          </li>
        ))}
      </ol>
    </MockCard>
  );
}

/* ── FAQ ────────────────────────────────────────────────────────────────── */

const FAQ: [string, string][] = [
  [
    "Will it spend money without me knowing?",
    "Not by default. Campaigns are created paused and wait for your approval. Every launch email has a one-tap pause link, every campaign has an end date, and your daily budget and monthly limit are hard caps.",
  ],
  [
    "Whose ad accounts does it use?",
    "Yours. You connect your own Google Ads account, and Microsoft Advertising if you want Bing. Google and Microsoft bill you directly for ad spend; it never goes through us.",
  ],
  [
    "How does it know where a storm hit?",
    "It reads official National Weather Service alerts, works out which ZIP codes fall inside the storm, and keeps only the ones inside the area you serve.",
  ],
  [
    "What about insurance wording?",
    "Ad copy is checked against rules for your state before it is published, and phone numbers are kept out of ad text. The call button carries the number instead.",
  ],
  [
    "Is it only for roofers?",
    "Roofers are the first trade we are building for. Plumbers after a flood, restoration crews and other weather-driven businesses use the same idea, and you are welcome to try it.",
  ],
  [
    "How do I start?",
    "Start the 7-day free trial and add your website. We will help you set up your first storm and check it with you before anything goes live.",
  ],
];

/* ── page ───────────────────────────────────────────────────────────────── */

const StormResponse = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Storm Response — Storm-triggered Google &amp; Bing Ads for Roofers | Upfixe</title>
        <meta
          name="description"
          content="Storm Response watches National Weather Service alerts and prepares Google and Microsoft search ads for the ZIP codes a storm hit, inside your service area. You set the budget and approve each campaign."
        />
        <link rel="canonical" href="https://upfixe.com/storm-response" />
        <meta property="og:title" content="Storm Response — after the storm, your phone rings" />
        <meta
          property="og:description"
          content="Weather-triggered Google and Microsoft search ads for roofers, built for the ZIPs a storm hit. You set the limits."
        />
      </Helmet>

      <Header />

      <main className="flex-grow">
        {/* 1. The outcome (hero) */}
        <section className="relative overflow-hidden bg-background pt-28 pb-8 md:pt-36">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-30" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
          <div className="container mx-auto px-4 relative z-10 max-w-6xl">
            <motion.div {...fade} className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium bg-primary/10 text-primary mb-6">
                  <CloudLightning className="h-3.5 w-3.5" />
                  7-day free trial · roofers and weather-driven trades
                </span>
                <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-[1.1] tracking-tight">
                  After the storm, <span className="text-primary">your phone rings.</span>
                </h1>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                  Storm Response watches National Weather Service alerts and prepares Google and
                  Microsoft search ads for the ZIP codes a storm actually hit, inside the area you
                  serve. Homeowners searching for emergency help find you. You set the budget and
                  approve each campaign.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button size="lg" asChild className="text-base px-7 h-12 font-semibold">
                    <a href={TRIAL_URL}>
                      Start your 7-day free trial <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                  <Button size="lg" variant="outline" asChild className="text-base px-7 h-12">
                    <a href="#safety">How you stay in control</a>
                  </Button>
                </div>
              </div>
              <PhoneMock />
            </motion.div>
          </div>
        </section>

        {/* 2. The tap and call */}
        <Section
          index={2}
          label="The tap and call"
          title={<>One tap from the search results to <span className="text-primary">your phone.</span></>}
          mock={<AdMock />}
        >
          <p>
            Someone with a damaged roof is not browsing. They search, they tap the number, they
            call whoever answers. Storm Response builds the ad for that moment: a call button
            with your number, headlines written for the storm, and a clean display address that
            says what you do.
          </p>
          <Ticks
            items={[
              "Call button on every ad, with calls tracked back to the campaign",
              "Sitelinks to your real pages: inspections, repairs, reviews, your city pages",
              "A landing page matched to where the storm hit, if you have one per city",
              "Choose phone calls, website visits, or both",
            ]}
          />
        </Section>

        {/* 3. The search intent */}
        <Section
          index={3}
          label="The search intent"
          flip
          tinted
          title={<>Ads for what people type <span className="text-primary">while the weather is still fresh.</span></>}
          mock={<IntentMock />}
        >
          <p>
            Each storm campaign has three ad groups, so the ad a homeowner sees matches the
            search: one for the weather itself, one for the place, one for your services and
            your name. Keywords come from your own landing page and real search data, and
            irrelevant searches are filtered out before you pay for them.
          </p>
          <Ticks
            items={[
              "Weather: damage, emergency and urgent-help searches for this storm",
              "Location: your city names and near-me searches",
              "Services: what you actually do, plus searches for your business name",
              "Negative keywords keep you out of job-seeker and DIY searches",
            ]}
          />
        </Section>

        {/* 4. The safety net */}
        <div id="safety" />
        <Section
          index={4}
          label="The safety net"
          title={<>You set the limits. <span className="text-primary">We stay inside them.</span></>}
          mock={<GuardrailMock />}
        >
          <p>
            Storm ads should never be a surprise on your card. Every limit is yours to set, and
            the defaults are the cautious ones.
          </p>
          <Ticks
            items={[
              "Campaigns are created paused and wait for your approval by default",
              "A daily budget per platform, and one monthly limit covering Google and Bing together",
              "Every storm campaign has an end date and stops on its own",
              "A one-tap pause link in every launch email",
              "Ads run in your own Google and Microsoft accounts and bill you directly",
            ]}
          />
        </Section>

        {/* 5. Zero manual work */}
        <Section
          index={5}
          label="Zero manual work"
          flip
          tinted
          title={<>Your website already says who you are. <span className="text-primary">We read it.</span></>}
          mock={<ScanMock />}
        >
          <p>
            Skip the long form. Point us at your website and we suggest your phone number, the
            selling points you already advertise (years in business, free inspections,
            certifications, warranties) and the city and service pages worth sending traffic to.
            You review everything and change what you like before anything is saved.
          </p>
          <Ticks
            items={[
              "Only claims found on your site are used, nothing is invented",
              "Ad copy, sitelinks and landing pages are generated from your pages and your answers",
              "Keywords and ad groups are rebuilt for each storm, not copied from a template",
            ]}
          />
        </Section>

        {/* 6. Reverse onboarding */}
        <Section
          index={6}
          label="Start in minutes"
          title={<>Get armed before the next storm, <span className="text-primary">not during it.</span></>}
          mock={<StepsMock />}
        >
          <p>
            The best time to set this up is when the sky is clear. Connect your accounts, tell us
            where you work and how you want to be reached, and Storm Response waits quietly until
            the next storm crosses your area.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Button size="lg" asChild className="text-base px-7 h-12 font-semibold">
              <a href={TRIAL_URL}>
                Start your 7-day free trial <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button size="lg" variant="outline" asChild className="text-base px-7 h-12">
              <a href={TRIAL_URL}>Already have an account? Log in</a>
            </Button>
          </div>
        </Section>

        {/* FAQ */}
        <section className="py-16 md:py-20 px-4 bg-muted/40 border-y">
          <div className="container mx-auto max-w-3xl">
            <motion.div {...fade}>
              <h2 className="text-3xl font-bold text-foreground mb-8 text-center">Common questions</h2>
              <div className="space-y-3">
                {FAQ.map(([q, a]) => (
                  <details
                    key={q}
                    className="group rounded-2xl border border-border bg-card px-5 py-4 open:shadow-md"
                  >
                    <summary className="cursor-pointer list-none font-semibold text-foreground flex items-center justify-between gap-4">
                      {q}
                      <Sparkles className="h-4 w-4 shrink-0 text-primary group-open:rotate-12 transition-transform" />
                    </summary>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
                  </details>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="py-20 px-4 bg-primary text-primary-foreground">
          <div className="container mx-auto max-w-3xl text-center space-y-5">
            <h2 className="text-3xl md:text-4xl font-bold">Be ready for the next storm</h2>
            <p className="opacity-90">
              Storm Response is new, so we set up your first storm with you and check it before
              anything goes live. We would rather show you real results than promise numbers today.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button size="lg" asChild className="text-base px-7 h-12 bg-background text-foreground hover:bg-background/90 font-semibold">
                <a href={TRIAL_URL}>Start your 7-day free trial</a>
              </Button>
              <Button size="lg" asChild className="text-base px-7 h-12 bg-white text-primary hover:bg-white/90 font-semibold">
                <Link to="/pricing">See Upfixe plans</Link>
              </Button>
            </div>
            <p className="text-xs opacity-80">Ad spend is billed by Google and Microsoft directly to your accounts.</p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StormResponse;
